// 평가 도메인 enum → 배지 매핑.
// status-badge.tsx와 같은 패턴으로, 색만으로 의미를 전달하지 않도록 항상 라벨을 병기한다.

import { Badge } from '~/components/ui/badge'
import type { BadgeTone } from '~/components/ui/badge'
import type {
    AssessmentClassification,
    AssessmentStatus,
    ItemVerdict,
    PassLikelihood,
} from '~/api/types'

const STATUS_MAP: Record<AssessmentStatus, { tone: BadgeTone; label: string }> =
    {
        collecting: { tone: 'info', label: '작성 중' },
        scored: { tone: 'accent', label: '채점 완료' },
        reported: { tone: 'success', label: '보고서 완료' },
    }

const VERDICT_MAP: Record<ItemVerdict, { tone: BadgeTone; label: string }> = {
    적합: { tone: 'success', label: '적합' },
    미흡: { tone: 'warning', label: '미흡' },
    부적합: { tone: 'danger', label: '부적합' },
}

const LIKELIHOOD_MAP: Record<
    PassLikelihood,
    { tone: BadgeTone; label: string }
> = {
    high: { tone: 'success', label: '합격 가능성 높음' },
    medium: { tone: 'warning', label: '합격 가능성 보통' },
    low: { tone: 'danger', label: '합격 가능성 낮음' },
}

const CLASSIFICATION_MAP: Record<
    AssessmentClassification,
    { tone: BadgeTone; label: string }
> = {
    선행요건: { tone: 'info', label: '선행요건' },
    HACCP관리: { tone: 'accent', label: 'HACCP관리' },
}

/** 평가 진행 상태 배지. */
export function StatusBadgeAssessment({
    status,
}: {
    status: AssessmentStatus
}) {
    const mapped = STATUS_MAP[status]
    return <Badge tone={mapped.tone}>{mapped.label}</Badge>
}

/** 항목 채점 판정 배지(적합/미흡/부적합). */
export function VerdictBadge({ verdict }: { verdict: ItemVerdict }) {
    const mapped = VERDICT_MAP[verdict]
    return <Badge tone={mapped.tone}>{mapped.label}</Badge>
}

/** 합격 가능성 배지. 값이 없으면 표시하지 않는다. */
export function PassLikelihoodBadge({
    likelihood,
}: {
    likelihood?: PassLikelihood | null
}) {
    const mapped = likelihood ? LIKELIHOOD_MAP[likelihood] : undefined
    if (!mapped) return null
    return <Badge tone={mapped.tone}>{mapped.label}</Badge>
}

/** 평가 분류 배지(선행요건/HACCP관리). 분류 미지정(과거 평가 등)이면 표시하지 않는다. */
export function ClassificationBadge({
    classification,
}: {
    classification?: AssessmentClassification | null
}) {
    const mapped = classification
        ? CLASSIFICATION_MAP[classification]
        : undefined
    if (!mapped) return null
    return <Badge tone={mapped.tone}>{mapped.label}</Badge>
}
